import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import moment from 'moment';
import { Dialog, Button, Message, Table } from '@alifd/next';
import { Box } from '@mui/material';
import { Input, InputRef } from 'antd';
import ReactDragListView from 'react-drag-listview';
import client from '@/lib/feathers';
import { ServiceDialogProps, CategoryMenu, TableData } from '../../types';

const AbsenceDialog: React.FC<ServiceDialogProps> = ({
  showDialog,
  setShowDialog,
}) => {
  // 操作資料
  const [clientData, setClientData] = useState<CategoryMenu['data']>([]);
  const [serviceData, setServiceData] = useState<CategoryMenu | null>(null);

  // 新增項目
  const [newServiceInputValue, setNewServiceInputValue] = useState('');
  const [newServiceInputStatus, setNewServiceInputStatus] = useState<
    '' | 'warning' | 'error'
  >('');
  const newServiceInputRef = useRef<InputRef>(null);

  // 編輯項目
  const [editingService, setEditingService] = useState<TableData | null>(null);
  const [editServiceInputStatus, setEditServiceInputStatus] = useState<
    '' | 'warning' | 'error'
  >('');
  const editServiceInputRef = useRef<InputRef>(null);

  const initializeState = useCallback(() => {
    setNewServiceInputValue('');
    setNewServiceInputStatus('');
    setEditingService(null);
    setEditServiceInputStatus('');
  }, []);

  useEffect(() => {
    (async () => {
      const res = await client.service('categoryMenu').find({
        query: {
          service: 'absenceType',
        },
      });
      setServiceData(res.data[0]);
      setClientData(res.data[0].data);
    })();
  }, []);

  const handleCreate = () => {
    if (newServiceInputValue) {
      setClientData((prev) => [
        {
          id: moment().valueOf().toString(),
          name: newServiceInputValue,
        },
        ...prev,
      ]);
      setNewServiceInputValue('');
    } else {
      setNewServiceInputStatus('error');
      Message.error('請輸入服務類型名稱');
      if (newServiceInputRef.current) {
        newServiceInputRef.current.focus();
      }
    }
  };

  const handleDragEnd = (fromIndex: number, toIndex: number) => {
    setClientData((prev) => {
      const newData = [...prev];
      const [movingItem] = newData.splice(fromIndex, 1);
      newData.splice(toIndex, 0, movingItem);
      return newData;
    });
  };

  const handleEdit = (record: TableData) => {
    setEditingService(record);
    if (editServiceInputRef.current) {
      editServiceInputRef.current.focus();
    }
  };

  const handleEditConfirm = useCallback(() => {
    if (editingService?.name) {
      setClientData((prev) => {
        return prev.map((data) => {
          if (data.id === editingService.id) {
            return {
              ...data,
              name: editingService.name,
            };
          }
          return data;
        });
      });
      setEditingService(null);
    } else {
      setEditServiceInputStatus('error');
      Message.error('請輸入服務類型名稱');
      if (editServiceInputRef.current) {
        editServiceInputRef.current.focus();
      }
    }
  }, [editingService]);

  const handleEditCancel = () => {
    setEditingService(null);
    setEditServiceInputStatus('');
  };

  const handleDelete = (id: string) => {
    setClientData((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSave = async () => {
    try {
      await client.service('categoryMenu').patch(serviceData?._id, {
        ...serviceData,
        data: clientData,
      });
      Message.success('儲存成功');
      // dispatch(fetchServicesThunk());
      initializeState();
      setShowDialog(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    initializeState();
    setShowDialog(false);
    Message.notice('已取消編輯');
  };

  const footer = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        justifySelf: 'flex-end',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          gap: '10px',
        }}
      >
        <Button type="primary" onClick={handleSave}>
          儲存
        </Button>
        <Button onClick={handleCancel}>取消</Button>
      </Box>
    </Box>
  );

  const dataSource: TableData[] = useMemo(
    () =>
      clientData.map((item) => ({
        id: item.id,
        name: item.name,
      })),
    [clientData]
  );

  const columns = useMemo(
    () => [
      {
        title: '服務類型名稱',
        dataIndex: 'name',
        cell: (name: string, _index: number, record: TableData) => {
          if (editingService?.id === record.id) {
            return (
              <Input
                placeholder="請輸入服務類型名稱"
                ref={editServiceInputRef}
                value={editingService.name}
                onChange={(e) => {
                  setEditingService((prev) =>
                    prev
                      ? {
                          ...prev,
                          name: e.target.value,
                        }
                      : null
                  );
                }}
                // onPressEnter={handleEditConfirm}
                status={editServiceInputStatus}
              />
            );
          } else {
            return name;
          }
        },
      },
      {
        title: '操作',
        dataIndex: 'id',
        cell: (id: string, _index: number, record: TableData) => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {editingService?.id !== id && (
                <>
                  <Button onClick={() => handleEdit(record)}>編輯</Button>
                  <Button
                    type="normal"
                    warning
                    onClick={() => handleDelete(id)}
                  >
                    刪除
                  </Button>
                </>
              )}
              {editingService?.id === id && (
                <>
                  <Button onClick={handleEditConfirm}>確認</Button>
                  <Button onClick={handleEditCancel}>取消</Button>
                </>
              )}
            </Box>
          );
        },
      },
    ],
    [
      editingService?.id,
      editingService?.name,
      editServiceInputStatus,
      handleEditConfirm,
    ]
  );

  useEffect(() => {
    if (editServiceInputRef.current) {
      editServiceInputRef.current.focus();
    }
  }, [columns]);

  return (
    <Dialog
      title="編輯服務類型"
      visible={showDialog}
      footer={footer}
      onClose={handleCancel}
    >
      <Box
        sx={{
          width: '400px',
          height: '450px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
          }}
        >
          <Input
            placeholder="請輸入服務類型名稱"
            value={newServiceInputValue}
            onChange={(e) => {
              setNewServiceInputValue(e.target.value);
              setNewServiceInputStatus('');
            }}
            // onPressEnter={handleCreate}
            ref={newServiceInputRef}
            style={{ flexGrow: 1 }}
            status={newServiceInputStatus}
          />
          <Button onClick={handleCreate}>新增</Button>
        </Box>
        <Box>
          <p>拖曳服務類型以改變順序</p>
          <ReactDragListView onDragEnd={handleDragEnd}>
            <Table
              dataSource={dataSource}
              columns={columns}
              fixedHeader
              useVirtual
              maxBodyHeight={350}
            />
          </ReactDragListView>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AbsenceDialog;
